import { guessLanguage } from "guesslanguage";
import jsPDF from "jspdf";

export class JsPdfLanguage {
  text = "";
  product = "";
  jsPdf = new jsPDF();
  constructor(fields?: Partial<JsPdfLanguage>) {
    Object.assign(this, fields);
  }
}

export const DEFAULT_FONT = "Montserrat";

const customFonts: Record<string, { module: string; fontName: string }> = {
  si: { module: "sinhala", fontName: "si" },
  sinhala: { module: "sinhala", fontName: "Sinhala" },
  ar: { module: "arabic", fontName: "Arabic" },
  farsi: { module: "arabic", fontName: "Farsi" },
  urdu: { module: "arabic", fontName: "Urdu" },
  hindi: { module: "notosans", fontName: "Hindi" },
  kazakh: { module: "notosans", fontName: "Kazakh" },
  greek: { module: "notosans", fontName: "Greek" },
  turkish: { module: "notosans", fontName: "Turkish" },
  vietnamese: { module: "notosans", fontName: "Vietnamese" }
};

export const importFont = async (
  lang: string,
  doc?: jsPDF | any
): Promise<string> => {
  try {
    const config = customFonts[lang.toLowerCase()];
    const moduleName = (config?.module || lang).toLowerCase();
    const fontName =
      config?.fontName || lang.charAt(0).toUpperCase() + lang.slice(1);

    const { downloadFontsJsPdf } = await import(
      `@/app/ui/assets/fonts/jsPDF/${moduleName}`
    );

    if (downloadFontsJsPdf) {
      await downloadFontsJsPdf(doc);
    }

    return fontName;
  } catch (error) {
    return DEFAULT_FONT;
  }
};

const determineLanguageName = (data: any[]): string => {
  const name = data?.[2] ?? data?.[0] ?? DEFAULT_FONT;
  return name === "unknown" ? DEFAULT_FONT : name;
};

export const getLanguageFromText = async (
  data: JsPdfLanguage
): Promise<string> => {
  let languageName = DEFAULT_FONT;
  let listFontNames: string[] = [];

  try {
    const language = await new Promise<any[]>((resolve) =>
      guessLanguage.info(data.text, (value: any) => resolve(value))
    );

    languageName = determineLanguageName(language);

    const availableFonts = data.jsPdf.getFontList();
    listFontNames = Array.from(
      Object.keys(availableFonts).map((font: string) => font)
    );
  } catch (error) {
    console.error("Error determining language:", error);
  }

  if (!listFontNames.includes(languageName)) {
    return await importFont(languageName, data.jsPdf);
  }

  return listFontNames.includes(languageName) ? languageName : DEFAULT_FONT;
};

const hasSegmenter =
  typeof Intl !== "undefined" && typeof (Intl as any).Segmenter !== "undefined";

const segmenter = hasSegmenter
  ? new (Intl as any).Segmenter("und", { granularity: "word" })
  : null;

export const getSegmentsText = (
  text: string
): Array<{ segment: string; isWordLike: boolean }> =>
  [...segmenter.segment(text)].map((item) => ({
    segment: item.segment,
    isWordLike: item.isWordLike === true
  }));

const INTERPACK_INTERNATIONAL_PRODUCTS = new Set([
  "INTERPACK",
  "INTERPACKMAXI"
]);

export const isInterpackInternationalProduct = (product?: string): boolean =>
  INTERPACK_INTERNATIONAL_PRODUCTS.has((product ?? "").toUpperCase());

export const detectLanguage = async (data: JsPdfLanguage): Promise<string> => {
  if (!isInterpackInternationalProduct(data.product)) {
    return DEFAULT_FONT;
  }
  return await getLanguageFromText(data);
};

export const checkIsInterpack = (product: string) =>
  isInterpackInternationalProduct(product);

export const specialLanguage = (language: string): boolean =>
  !!language.toLowerCase().match(/^chinese$|^japanese$|^korean$/);
